import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';

const statusConfig = {
  Pending: { color: '#f59e0b', bg: '#fffbeb' },
  'In Progress': { color: '#2563eb', bg: '#eff6ff' },
  Completed: { color: '#16a34a', bg: '#ecfdf3' },
  Overdue: { color: '#dc2626', bg: '#fef2f2' },
};

const priorities = ['Low', 'Medium', 'High', 'Critical'];
const statuses = ['Pending', 'In Progress', 'Completed', 'Overdue'];

const initialTasks = [
  {
    id: 'TSK-001',
    taskName: 'Finalize CRM onboarding deck',
    dueDate: '2026-06-12',
    assignedTo: 'Ava Wilson',
    relatedRecord: 'Acme Corporation',
    priority: 'High',
    status: 'In Progress',
  },
  {
    id: 'TSK-002',
    taskName: 'Follow up on proposal signature',
    dueDate: '2026-06-10',
    assignedTo: 'Noah Bennett',
    relatedRecord: 'BrightPath Logistics',
    priority: 'Critical',
    status: 'Pending',
  },
  {
    id: 'TSK-003',
    taskName: 'Prepare QBR insights summary',
    dueDate: '2026-06-08',
    assignedTo: 'Emma Reed',
    relatedRecord: 'Nova Health Systems',
    priority: 'Medium',
    status: 'Overdue',
  },
  {
    id: 'TSK-004',
    taskName: 'Update opportunity risk tracker',
    dueDate: '2026-06-07',
    assignedTo: 'Liam Carter',
    relatedRecord: 'GreenCore Energy',
    priority: 'Low',
    status: 'Completed',
  },
  {
    id: 'TSK-005',
    taskName: 'Validate pricing matrix with finance',
    dueDate: '2026-06-14',
    assignedTo: 'Olivia Brooks',
    relatedRecord: 'Enterprise Solutions Group',
    priority: 'High',
    status: 'In Progress',
  },
];

const calendarDays = Array.from({ length: 30 }, (_, i) => i + 1);

export const Tasks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tasks, setTasks] = useState(initialTasks);
  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openNewTask, setOpenNewTask] = useState(false);
  const [newTask, setNewTask] = useState({
    taskName: '',
    dueDate: '',
    assignedTo: '',
    relatedRecord: '',
    priority: 'Medium',
    status: 'Pending',
  });

  const filteredTasks = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tasks.filter(task => {
      const matchesSearch =
        !term ||
        [task.taskName, task.assignedTo, task.relatedRecord].some(value =>
          (value || '').toLowerCase().includes(term)
        );
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [tasks, search, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: filteredTasks.length,
      pending: filteredTasks.filter(task => task.status === 'Pending').length,
      inProgress: filteredTasks.filter(task => task.status === 'In Progress').length,
      completed: filteredTasks.filter(task => task.status === 'Completed').length,
      overdue: filteredTasks.filter(task => task.status === 'Overdue').length,
    };
  }, [filteredTasks]);

  const groupedByStatus = useMemo(() => {
    return statuses.reduce((acc, status) => {
      acc[status] = filteredTasks.filter(task => task.status === status);
      return acc;
    }, {});
  }, [filteredTasks]);

  const tasksByDay = useMemo(() => {
    const map = {};
    filteredTasks.forEach(task => {
      const day = Number(task.dueDate.split('-')[2]);
      if (!map[day]) map[day] = [];
      map[day].push(task);
    });
    return map;
  }, [filteredTasks]);

  const handleCreateTask = () => {
    if (!newTask.taskName || !newTask.dueDate || !newTask.assignedTo || !newTask.relatedRecord) return;

    const task = {
      id: `TSK-${Date.now()}`,
      ...newTask,
    };

    setTasks(prev => [task, ...prev]);
    setOpenNewTask(false);
    setNewTask({
      taskName: '',
      dueDate: '',
      assignedTo: '',
      relatedRecord: '',
      priority: 'Medium',
      status: 'Pending',
    });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: '#f6f8fc', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <TaskAltIcon sx={{ color: '#7b1fa2', fontSize: 30 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#4a1368' }}>
                CRM Tasks
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Team execution workspace with list, kanban, and calendar tracking
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setOpenNewTask(true)}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#651a86' } }}
          >
            New Task
          </Button>
        </Box>

        <Grid container spacing={1.25} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 1.5, borderRadius: 2.5, borderLeft: '4px solid #334155' }}>
              <Typography variant="caption" color="text.secondary">Total Tasks</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 1.5, borderRadius: 2.5, borderLeft: '4px solid #f59e0b' }}>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.pending}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 1.5, borderRadius: 2.5, borderLeft: '4px solid #2563eb' }}>
              <Typography variant="caption" color="text.secondary">In Progress</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.inProgress}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 1.5, borderRadius: 2.5, borderLeft: '4px solid #16a34a' }}>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.completed}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={2.4}>
            <Paper sx={{ p: 1.5, borderRadius: 2.5, borderLeft: '4px solid #dc2626' }}>
              <Typography variant="caption" color="text.secondary">Overdue</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{stats.overdue}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search task, assignee, related record"
            value={search}
            onChange={event => setSearch(event.target.value)}
            sx={{ minWidth: isMobile ? '100%' : 320, bgcolor: '#fff' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180, bgcolor: '#fff' }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={event => setStatusFilter(event.target.value)}>
              <MenuItem value="all">All Status</MenuItem>
              {statuses.map(status => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, mb: 2 }}>
          <Tabs
            value={view}
            onChange={(_, value) => setView(value)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{ p: 1, '& .MuiTabs-flexContainer': { gap: 0.8 } }}
          >
            <Tab value="list" label="List View" sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, '&.Mui-selected': { bgcolor: '#ede9fe', color: '#6b21a8' } }} />
            <Tab value="kanban" label="Kanban View" sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, '&.Mui-selected': { bgcolor: '#ede9fe', color: '#6b21a8' } }} />
            <Tab value="calendar" label="Calendar View" sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, '&.Mui-selected': { bgcolor: '#ede9fe', color: '#6b21a8' } }} />
          </Tabs>
        </Paper>

        {view === 'list' && (
          <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#fff' }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Task Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Assigned To</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Related Record</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: '#64748b' }}>
                      No tasks found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTasks.map(task => (
                    <TableRow key={task.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{task.taskName}</TableCell>
                      <TableCell>{task.dueDate}</TableCell>
                      <TableCell>{task.assignedTo}</TableCell>
                      <TableCell>{task.relatedRecord}</TableCell>
                      <TableCell>
                        <Chip label={task.priority} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.status}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            color: statusConfig[task.status].color,
                            bgcolor: statusConfig[task.status].bg,
                            border: `1px solid ${statusConfig[task.status].color}33`,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {view === 'kanban' && (
          <Grid container spacing={1.25}>
            {statuses.map(status => (
              <Grid item xs={12} sm={6} md={3} key={status}>
                <Paper sx={{ p: 1.25, borderRadius: 2.5, bgcolor: statusConfig[status].bg, border: `1px solid ${statusConfig[status].color}33` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.1 }}>
                    <Typography sx={{ fontWeight: 800, color: statusConfig[status].color }}>{status}</Typography>
                    <Chip label={groupedByStatus[status]?.length || 0} size="small" sx={{ fontWeight: 700, bgcolor: '#fff' }} />
                  </Box>

                  <Stack spacing={1}>
                    {(groupedByStatus[status] || []).length === 0 ? (
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff' }}>
                        <Typography variant="body2" color="text.secondary">No tasks</Typography>
                      </Paper>
                    ) : (
                      groupedByStatus[status].map(task => (
                        <Paper key={task.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff' }}>
                          <Typography sx={{ fontWeight: 700, mb: 0.8 }}>{task.taskName}</Typography>
                          <Stack spacing={0.45}>
                            <Typography variant="caption" color="text.secondary">Due: {task.dueDate}</Typography>
                            <Typography variant="caption" color="text.secondary">Assigned: {task.assignedTo}</Typography>
                            <Typography variant="caption" color="text.secondary">Record: {task.relatedRecord}</Typography>
                          </Stack>
                          <Box sx={{ mt: 0.8 }}>
                            <Chip size="small" label={task.priority} variant="outlined" sx={{ fontWeight: 700 }} />
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {view === 'calendar' && (
          <Paper sx={{ p: 1.2, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
              <CalendarMonthOutlinedIcon sx={{ color: '#7b1fa2' }} />
              <Typography sx={{ fontWeight: 800, color: '#4a1368' }}>June 2026</Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 0.8,
              }}
            >
              {calendarDays.map(day => {
                const dayTasks = tasksByDay[day] || [];
                return (
                  <Paper
                    key={day}
                    variant="outlined"
                    sx={{
                      p: 0.8,
                      minHeight: 92,
                      borderRadius: 1.8,
                      bgcolor: dayTasks.length ? '#faf5ff' : '#fff',
                      borderColor: dayTasks.length ? '#d8b4fe' : '#e2e8f0',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155' }}>{day}</Typography>
                    <Stack spacing={0.4} sx={{ mt: 0.5 }}>
                      {dayTasks.slice(0, 2).map(task => (
                        <Chip
                          key={task.id}
                          size="small"
                          label={task.taskName}
                          sx={{
                            height: 20,
                            '& .MuiChip-label': {
                              px: 0.6,
                              fontSize: '0.63rem',
                              maxWidth: 115,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            bgcolor: statusConfig[task.status].bg,
                            color: statusConfig[task.status].color,
                          }}
                        />
                      ))}
                      {dayTasks.length > 2 && (
                        <Typography variant="caption" sx={{ color: '#64748b' }}>+{dayTasks.length - 2} more</Typography>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Box>
          </Paper>
        )}
      </Paper>

      <Dialog open={openNewTask} onClose={() => setOpenNewTask(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>New Task</DialogTitle>
        <DialogContent>
          <Stack spacing={1.2} sx={{ mt: 1 }}>
            <TextField
              label="Task Name"
              value={newTask.taskName}
              onChange={event => setNewTask(prev => ({ ...prev, taskName: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Due Date"
              type="date"
              value={newTask.dueDate}
              onChange={event => setNewTask(prev => ({ ...prev, dueDate: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Assigned To"
              value={newTask.assignedTo}
              onChange={event => setNewTask(prev => ({ ...prev, assignedTo: event.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <TextField
              label="Related Record"
              value={newTask.relatedRecord}
              onChange={event => setNewTask(prev => ({ ...prev, relatedRecord: event.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.2 }}>
              <FormControl>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newTask.priority}
                  label="Priority"
                  onChange={event => setNewTask(prev => ({ ...prev, priority: event.target.value }))}
                >
                  {priorities.map(priority => (
                    <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newTask.status}
                  label="Status"
                  onChange={event => setNewTask(prev => ({ ...prev, status: event.target.value }))}
                >
                  {statuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewTask(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>
          <Button onClick={handleCreateTask} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#651a86' } }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tasks;
